import React, { type ReactNode, useMemo } from 'react';

import { createPortal } from 'react-dom';
import { graphql, useFragment } from 'react-relay';

import { ConfluenceIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { ConversationStartersBanner } from '@atlassian/search-ai/conversation-starters-banner';
import { getSAINLayerFrontendExperiment } from '@atlassian/search-ai/get-sain-layer-frontend-experiment';
import { NavigationalCard } from '@atlassian/search-ai/navigational-card';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import {
	type SearchResultType as ConfluenceSearchResultType,
	type SearchConfluenceSubtype,
	SearchConfluenceSubtype as SearchConfluenceSubtypeValue,
} from '@atlassian/search-client/common/types';
import { getConfluenceUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';

import { EVENT_SOURCE } from '../../../../../../common/constants/analytics';
import { ApplicationModes } from '../../../../../../common/constants/application-modes';
import { ProductKeys } from '../../../../../../common/constants/products';
import type { SearchResultProps } from '../../../../../../common/types';
import { useAppContext } from '../../../../../../common/ui/app-context';
import { getCommentCount } from '../../../../../../common/ui/comment-count';
import { ConfluenceResultMeta } from '../../../../../../common/ui/confluence-result-meta';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { getLikeCount } from '../../../../../../common/ui/like-count';
import { getLinksFromText } from '../../../../../../common/ui/link-attachments';
import {
	ChildLinkedResults,
	MentionsLinkedResults,
	SimilarLinkedResults,
} from '../../../../../../common/ui/linked-results';
import { getConfluenceEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchMetadata } from '../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { BreadcrumbSocialSignalNext } from '../../../../../../common/ui/social-signals';
import { SummarizeInChatButton } from '../../../../../../common/ui/summarize-in-chat-button';
import { Verified } from '../../../../../../common/ui/verified';
import { areLLMPoweredFeaturesEnabled } from '../../../../../../common/utils/entitlement-checks';
import { type AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { useCommonAndPrivateAttributes } from '../../../../../../controllers/analytics';
import { ErrorBoundary } from '../../../../../../controllers/error-boundary';
import { logException } from '../../../../../../controllers/error-boundary/log-exception';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useSmartAnswersIntentStore } from '../../../../../../controllers/smart-answers-intent-store';
import { useFeatures } from '../../../../../../controllers/store/bootstrap';

import type { searchConfluencePageBlogAttachment_AtlassianSearchPage$key } from './__generated__/searchConfluencePageBlogAttachment_AtlassianSearchPage.graphql';

export const isSearchConfluencePageBlogAttachment = (
	node?: any,
): node is searchConfluencePageBlogAttachment_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchConfluencePageBlogAttachment';
};

export type SearchConfluencePageBlogAttachmentProps =
	SearchResultProps<searchConfluencePageBlogAttachment_AtlassianSearchPage$key> & {
		is3pGroupingEnabled?: boolean;
		smartAnswersPortalRef?: React.RefObject<HTMLDivElement>;
	};

export const SearchConfluencePageBlogAttachment = ({
	node,
	position,
	cloudId,
	searchQuery: query,
	searchSessionId,
	selectedProduct,
	primaryProduct,
	appMode,
	thirdPartyConfigs,
	is3pGroupingEnabled,
	smartAnswersPortalRef,
	mainContentWidth,
}: SearchConfluencePageBlogAttachmentProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchConfluencePageBlogAttachment_AtlassianSearchPage on SearchConfluencePageBlogAttachment
			@argumentDefinitions(
				includeIconUrl: { type: "Boolean", defaultValue: false }
				includeNavBoostScore: { type: "Boolean", defaultValue: false }
			) {
				__typename
				id
				description
				title
				type
				url
				lastModified
				isVerified
				iconUrl @include(if: $includeIconUrl)
				navBoostScore @include(if: $includeNavBoostScore)
				space {
					id
					name
					webUiLink
				}
				confluenceEntity {
					... on ConfluencePage {
						subtype
						commentCountSummary {
							total
						}
						likesSummary {
							count
						}
					}
				}
				socialSignal(tenantId: $tenantId) {
					signal
					timestamp
					user {
						accountId
						name
						picture
						... on AtlassianAccountUser {
							email
							extendedProfile {
								jobTitle
							}
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const [features] = useFeatures();
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const { getFeatureGate, isAdminHubAIEnabled, isRovoLLMEnabled, onNavigate } = useAppContext();
	const { isSAINNavigationalExperienceEligible, getWorkspaceId } = useSmartAnswersIntentStore();

	const isSideBySideEnabled = features?.sideBySide?.enabled;
	const isLiteMode = features?.lite?.enabled ?? false;

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(data.id);
		let containerId: string | null = null;
		if (data.space?.id) {
			const [, , , spaceId] = getProductAndIDFromARI(data.space.id);
			containerId = spaceId;
		}

		return {
			id: data.id,
			containerId,
			spaceId: containerId,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
			isSideBySideEnabled,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data, isSideBySideEnabled]);

	const isSAINNavigationalExperienceEnabled = isSAINNavigationalExperienceEligible({
		position,
		url: data.url,
		navBoostScore: data.navBoostScore,
	});

	const onResultClicked = useFireResultClicked({
		metadata,
		position,
		socialSignal: data.socialSignal?.signal || undefined,
	});

	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
		socialSignal: data.socialSignal?.signal || undefined,
	});

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const subtype = data.confluenceEntity?.subtype;

	const metadataParts: [string, ReactNode][] = [
		[
			'type',
			<SearchResultType
				type={data.type as ConfluenceSearchResultType}
				subtype={subtype as SearchConfluenceSubtype}
			/>,
		],
		[
			'breadcrumbs',
			data.space?.name && (
				<ConfluenceResultMeta
					name={data.space.name}
					url={data.url}
					webUiLink={data.space?.webUiLink}
				/>
			),
		],
		['last-modified', data.lastModified && <LastModified lastModified={data.lastModified} />],
	];

	const socialMetadataParts: [string, ReactNode][] = [
		['comment-count', getCommentCount(data.confluenceEntity?.commentCountSummary?.total)],
		['like-count', getLikeCount(data.confluenceEntity?.likesSummary?.count)],
	];

	const titleMetadataParts: [string, ReactNode][] = [['verified', data.isVerified && <Verified />]];

	if (data.socialSignal && appMode === ApplicationModes.Rovo) {
		const { user, signal, timestamp } = data.socialSignal;
		socialMetadataParts.push([
			'social-signal',
			user?.email && (
				<BreadcrumbSocialSignalNext
					userAccountId={user.accountId}
					userName={user.name}
					userEmail={user.email}
					userPicture={user.picture}
					userJobTitle={user.extendedProfile?.jobTitle}
					signal={signal}
					timestamp={timestamp}
					commonAttributes={commonAttributes}
				/>
			),
		]);
	}

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const hasSimilarResults = linkedResults?.some(
		(linkedResult) => linkedResult.category?.toUpperCase() === 'SIMILAR',
	);
	const hasChildResults = linkedResults?.some(
		(linkedResult) => linkedResult.category?.toUpperCase() === 'CHILD',
	);

	const extractedLinksFromText = getLinksFromText(data.description);
	const hasExtractedLinks = extractedLinksFromText && extractedLinksFromText.length > 0;
	const hasRelatedLinks = (linkedResults && linkedResults.length > 0) || hasExtractedLinks;

	const { serveExperiment: serveConversationStartersExperiment } = getSAINLayerFrontendExperiment({
		abTestId: 'kd_convo_starters_in_search',
	});

	const isConversationStartersEnabled = getFeatureGate('kd_convo_starters_dogfooding');

	const conversationStartersIfEnabled =
		position === 0 &&
		appMode === ApplicationModes.Rovo &&
		areLLMPoweredFeaturesEnabled(appMode, isAdminHubAIEnabled, isRovoLLMEnabled) &&
		['page', 'blogpost'].includes(data.type) &&
		!!(serveConversationStartersExperiment || isConversationStartersEnabled)
			? { ari: data.id, url: data.url }
			: null;

	// In lite mode, instead of rendering the AI "Summarize" pill banner above
	// the related links footer, reuse the existing SummarizeInChatButton with
	// default Atlaskit Button spacing (32px tall) and place it on the same line
	// as the Related links toggle. The button reuses the established analytics
	// callback + Rovo Chat publish flow used by the per-row hover Summarize.
	const shouldRenderLiteSummarizeButton = isLiteMode && !!conversationStartersIfEnabled;
	const liteSummarizeButton = shouldRenderLiteSummarizeButton ? (
		<SummarizeInChatButton
			url={data.url}
			testId="search-result-lite-summarize-in-chat-button"
			onClick={onSummarizeInChatButtonClicked}
			spacing="default"
		/>
	) : null;

	let icon: React.JSX.Element;
	if (fg('rovo_search_confluence_icons_fps')) {
		icon = getConfluenceEntityIcon({
			iconUrl: data.iconUrl,
			type: data.type,
			size: 'medium',
			isLiveDoc: data.confluenceEntity?.subtype === SearchConfluenceSubtypeValue.LIVE,
		});
	} else {
		icon = <ConfluenceIcon size="medium" shouldUseNewLogoDesign />;
	}

	const searchResultComponent = (
		<SearchResult
			{...data}
			product={ProductKeys.Confluence}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={getConfluenceUrlWithAnalyticsQueryParams({
				url: data.url,
				query,
				searchSessionId,
			})}
			title={enrichExcerpt(data.title)}
			icon={icon}
			headerMetadataParts={metadataParts}
			headerMetadataSocialParts={socialMetadataParts}
			titleMetadata={<SearchMetadata parts={titleMetadataParts} displaySpacer={false} />}
			description={<DescriptionBody description={processExcerpt(data.description)} />}
			hideSummarizeInChat={!!conversationStartersIfEnabled}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			hasRelatedLinks={hasRelatedLinks}
			footer={
				<Box>
					{cloudId && conversationStartersIfEnabled && !isLiteMode && (
						<ConversationStartersBanner
							primaryProduct={primaryProduct}
							cloudId={cloudId}
							searchResult={conversationStartersIfEnabled}
							analyticsAttributes={{ resultMetadata: metadata, ...commonAttributes }}
						/>
					)}
					{hasSimilarResults ? (
						<SimilarLinkedResults
							linkedResults={linkedResults || []}
							thirdPartyConfigs={thirdPartyConfigs}
							partial={data}
							url={getConfluenceUrlWithAnalyticsQueryParams({
								url: data.url,
								query,
								searchSessionId,
							})}
							position={position}
							onResultClicked={onResultClicked}
							isAllProductSelected={!selectedProduct}
							onResultContextMenu={onResultCtxMenuClicked}
							is3pGroupingEnabled={is3pGroupingEnabled}
							product={ProductKeys.Confluence}
							additionalFooterButton={isLiteMode ? liteSummarizeButton : undefined}
						/>
					) : hasChildResults ? (
						<ChildLinkedResults
							linkedResults={linkedResults || []}
							thirdPartyConfigs={thirdPartyConfigs}
							partial={data}
							url={getConfluenceUrlWithAnalyticsQueryParams({
								url: data.url,
								query,
								searchSessionId,
							})}
							position={position}
							onResultClicked={onResultClicked}
							onResultContextMenu={onResultCtxMenuClicked}
							additionalFooterButton={isLiteMode ? liteSummarizeButton : undefined}
						/>
					) : (
						<MentionsLinkedResults
							linkedResults={linkedResults || []}
							thirdPartyConfigs={thirdPartyConfigs}
							partial={data}
							url={getConfluenceUrlWithAnalyticsQueryParams({
								url: data.url,
								query,
								searchSessionId,
							})}
							position={position}
							onResultClicked={onResultClicked}
							onResultContextMenu={onResultCtxMenuClicked}
							isAllProductSelected={!selectedProduct}
							additionalFooterButton={isLiteMode ? liteSummarizeButton : undefined}
						/>
					)}
				</Box>
			}
			mainContentWidth={mainContentWidth}
		/>
	);

	// the SAIN Hero Nav card is rendered at the top of the search results via a portal replacing the first blue link
	if (isSAINNavigationalExperienceEnabled && smartAnswersPortalRef?.current) {
		const workspaceId = getWorkspaceId();
		return createPortal(
			<ErrorBoundary
				section="search-page-smart-answers-nav-card"
				fallback={searchResultComponent}
				onException={(error) => {
					logException(error);
				}}
			>
				<Box id="search-page-smart-answers">
					<NavigationalCard
						searchResultData={{
							...data,
							url: getConfluenceUrlWithAnalyticsQueryParams({
								url: data.url,
								query,
								searchSessionId,
							}),
							query,
							searchSessionId,
							cloudId: cloudId || '',
							areLLMPoweredFeaturesEnabled: areLLMPoweredFeaturesEnabled(
								appMode,
								isAdminHubAIEnabled,
								isRovoLLMEnabled,
							),
							primaryProduct: primaryProduct ?? 'unknown',
							extractedLinks: getLinksFromText(data.description),
							linkedResults,
						}}
						onNavigate={onNavigate}
						source={EVENT_SOURCE}
						workspaceId={workspaceId ?? ''}
						navBoostScore={fg('kd_enable_nav_boost_score') ? data.navBoostScore : undefined}
						searchPageAnalyticsCallbacks={{
							onResultClicked: onResultClicked,
							onResultContextMenu: onResultCtxMenuClicked,
							onCopyLinkButtonClicked: onCopyLinkButtonClicked,
						}}
					/>
				</Box>
			</ErrorBoundary>,
			smartAnswersPortalRef.current,
			'search-page-smart-answers-nav-card',
		);
	}

	return searchResultComponent;
};
